import { z } from 'zod'

export const VISIBILITY = ['public', 'authenticated', 'internal'] as const
export const AUDIENCES = ['author-admin', 'end-user', 'operator'] as const

export type Visibility = (typeof VISIBILITY)[number]
export type Audience = (typeof AUDIENCES)[number]

/** A tour anchor id: page, region, control, dotted (`inbox.triage.approve`). */
export const TOUR_ANCHOR_ID = /^[a-z][a-z0-9-]*(\.[a-z][a-z0-9-]*){1,3}$/

/** One or two sentences: at most two sentence-ending stops. */
const sentences = (text: string) => (text.trim().match(/[.!?](\s|$)/g) ?? []).length

/**
 * SA-1314 (tours spec §4.1): one page-tour step. `reveal` names an anchor opened
 * first (a folded section, a closed disclosure, a tab); `when_missing` says why the
 * control may be absent.
 */
export const tourStepSchema = z.object({
  anchor: z.string().regex(TOUR_ANCHOR_ID, 'a tour anchor is a dotted id such as inbox.triage.approve'),
  text: z.string().min(1).refine((t) => sentences(t) <= 2, 'a tour step is one or two sentences'),
  reveal: z.string().regex(TOUR_ANCHOR_ID).optional(),
  when_missing: z.string().min(1).optional(),
}).strict()

export type TourStep = z.infer<typeof tourStepSchema>

/**
 * Schema for the YAML frontmatter on every docs MDX file.
 * `visibility` defaults to 'authenticated' (most docs are in-product).
 * `feature` (optional) gates the whole page on a deployment feature flag.
 */
export const docFrontmatterSchema = z.object({
  title: z.string().min(1),
  summary: z.string().min(1),
  visibility: z.enum(VISIBILITY).default('authenticated'),
  feature: z.string().min(1).optional(),
  audience: z.enum(AUDIENCES),
  section: z.string().min(1),
  order: z.number().default(100),
  last_verified: z.string().min(1),
  owner: z.string().min(1),
  /** SA-1314: the app routes this page describes (a page guide's own page). */
  routes: z.array(z.string().regex(/^\/[^\s?#]*$/, 'a route is an app path such as /inbox')).min(1).optional(),
  /** SA-1314: other words people use for what this page covers ("emails", "jobs"). */
  aliases: z.array(z.string().min(1)).optional(),
  /** SA-1314: the page tour, three to eight steps, on a page guide only. */
  tour: z.array(tourStepSchema).min(3).max(8).optional(),
  /** SA-1314: written by the build from the app map, not by a person. */
  generated: z.boolean().default(false),
}).superRefine((fm, ctx) => {
  if (fm.tour && !fm.routes) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['tour'], message: 'a tour needs the routes it runs on' })
  }
})

export type DocFrontmatter = z.infer<typeof docFrontmatterSchema>

/** Parse + validate raw frontmatter. Throws ZodError on invalid input. */
export function parseFrontmatter(raw: unknown): DocFrontmatter {
  return docFrontmatterSchema.parse(raw)
}
